import { CURRENT_SPORT, SUPPORTED_SPORTS } from "constants/globalConstants";
import {HIGHLIGHT_OPTION_SHEMA, KeyMomentSetting} from "../types/keyMomentTypes";

export interface KeyMomentFilters {
    items: {
        type: string;
        attributes: string[];
        exclude_var: boolean;
        exclude_replay: boolean;
        include_type: boolean;
    }[];
}

const PERIOD_ORDER = [
    'PRE_GAME',
    'PRE_MATCH',
    'FIRST_HALF',
    'HALF_TIME',
    'SECOND_HALF',
    'FIRST_PERIOD_OF_EXTRA_TIME',
    'SECOND_PERIOD_OF_EXTRA_TIME',
    'PENALTY_SHOOT_OUT',
    'POST_MATCH',
    'POST_GAME',
];

const getPeriodRank = (period: unknown): number => {
    const normalizedPeriod = typeof period === 'string' ? period.trim() : '';
    const index = PERIOD_ORDER.indexOf(normalizedPeriod);
    return index === -1 ? PERIOD_ORDER.length : index;
};

export const getDistinctEvents = (elementList: any) => {
    return elementList
        .filter((el: any) => el.idEvent !== undefined)
        .filter((el: any, index: number, sortedList: any[]) => index === 0 || el.idEvent !== sortedList[index-1].idEvent)
        .map((el:any) => {return { 'idEvent': el.idEvent, 'eyebrow': el.eyebrow, 'home_team': el.event?.home_team || el.home_team, 'away_team': el.event?.away_team || el.away_team }})
    }

export function sortObjects<T, K extends keyof T>(
    array: T[],
    key: K | string, // Permetto string per supportare la chiave "global_seconds" anche se non mappata direttamente nel tipo
    direction: string = 'asc'
): T[] {
    // Funzione helper per parsare il play_time "mm:ss" o "ss"
    const parsePlayTime = (pt: unknown): number | null => {
        if (typeof pt === 'number') return pt;
        if (!pt || typeof pt !== 'string') return null; // Fallback se play_time è nullo o non valido

        const parts = pt.split(':');
        if (parts.length === 2) {
            const minutes = parseInt(parts[0], 10) || 0;
            const seconds = parseInt(parts[1], 10) || 0;
            return (minutes * 60) + seconds;
        } else {
            return parseInt(parts[0], 10) || 0;
        }
    };

    return [...array].sort((a:any, b:any) => {
        // Estraiamo i valori base, facendo il cast ad any per flessibilità
        let valueA: any = (a as any)[key];
        let valueB: any = (b as any)[key];

        if (key === 'event_time') {
            const dateA = a.event_time;
            const dateB = b.event_time;

            // 1. Ordinamento primario: event_time
            if (dateA == null && dateB != null) return direction === 'asc' ? 1 : -1;
            if (dateA != null && dateB == null) return direction === 'asc' ? -1 : 1;

            if (dateA != null && dateB != null) {
                const timeA = new Date(dateA).getTime();
                const timeB = new Date(dateB).getTime();

                if (timeA < timeB) return direction === 'asc' ? -1 : 1;
                if (timeA > timeB) return direction === 'asc' ? 1 : -1;
            }

            // 2. Ordinamento secondario: idEvent (a parità di date)
            const idA = a.idEvent;
            const idB = b.idEvent;

            if (idA !== idB) {
                if (idA == null) return direction === 'asc' ? 1 : -1;
                if (idB == null) return direction === 'asc' ? -1 : 1;

                if (typeof idA === 'string' && typeof idB === 'string') {
                    const compId = idA.localeCompare(idB);
                    return direction === 'asc' ? compId : -compId;
                }

                if (idA < idB) return direction === 'asc' ? -1 : 1;
                if (idA > idB) return direction === 'asc' ? 1 : -1;
            }

            // 3. Ordinamento terziario: play_time (a parità di date e di id)
            // Nota: se play_time è nel formato "mm:ss", puoi usare la tua funzione parsePlayTime(a.play_time) vista in precedenza
            const ptA = a.play_time; 
            const ptB = b.play_time;

            if (ptA !== ptB) {
                if (ptA == null) return direction === 'asc' ? 1 : -1;
                if (ptB == null) return direction === 'asc' ? -1 : 1;

                if (typeof ptA === 'string' && typeof ptB === 'string') {
                    const compPt = ptA.localeCompare(ptB);
                    return direction === 'asc' ? compPt : -compPt;
                }

                if (ptA < ptB) return direction === 'asc' ? -1 : 1;
                if (ptA > ptB) return direction === 'asc' ? 1 : -1;
            }

            // Se sono perfettamente identici su event_time, idEvent e play_time
            return 0;
        }

        // ─── OVERRIDES DI LOGICA SPECIFICA ───────────────────────────────────────
        // Override se la chiave di ordinamento è "global_seconds"
        if (key === 'global_seconds' && CURRENT_SPORT !== SUPPORTED_SPORTS.VOLLEY) {
            const periodRankA = getPeriodRank((a as any).period);
            const periodRankB = getPeriodRank((b as any).period);
            if (periodRankA !== periodRankB) {
                const periodComparison = periodRankA - periodRankB;
                return direction === 'asc' ? periodComparison : -periodComparison;
            }

            valueA = parsePlayTime(valueA);
            valueB = parsePlayTime(valueB);
        }

        // For football cards, play_time is period-relative. Compare the period
        // first so penalty shoot-outs cannot be placed before the first half.
        if (key === 'play_time' && CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL) {
            const periodRankA = getPeriodRank((a as any).period);
            const periodRankB = getPeriodRank((b as any).period);
            if (periodRankA !== periodRankB) {
                const periodComparison = periodRankA - periodRankB;
                return direction === 'asc' ? periodComparison : -periodComparison;
            }

            valueA = parsePlayTime(valueA);
            valueB = parsePlayTime(valueB);
        }

        if (valueA === valueB) return 0;
        // Gestione dei valori nulli in base alla direzione
        if (valueA == null) return direction === 'asc' ? 1 : -1;
        if (valueB == null) return direction === 'asc' ? -1 : 1;

        if (typeof valueA === 'string' && typeof valueB === 'string') {
            const comparison = valueA.localeCompare(valueB);
            return direction === 'asc' ? comparison : -comparison;
        }

        if (valueA < valueB) {
            return direction === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
            return direction === 'asc' ? 1 : -1;
        }

        return 0;
    });
}

export function isSortedBy<T extends { id?: unknown }>(array: T[], key: string | null, direction: string = 'asc'): boolean {
    if (!key || array.length === 0) return false;
    if (array.some((item) => (item as any)[key] == null)) return false;

    const sorted = sortObjects(array, key, direction);
    return sorted.every((item, index) => item.id === array[index]?.id);
}

export const applyKeyMomentFilter = (settings: KeyMomentSetting[]): KeyMomentFilters => {
    const items = settings
        .filter(km => km.selected)
        .flatMap(km => {
            const baseItem = {
                type: km.name,
                attributes: [] as string[],
                exclude_var: false,
                exclude_replay: false,
                include_type: false
            };

            let isRedOnly = false;

            if (km.options && !Array.isArray(km.options) && km.options.values) {

                if (km.options.schema === HIGHLIGHT_OPTION_SHEMA.CHECKBOX) {
                    km.options.values.forEach(opt => {
                        if (opt.name === "VAR" && !opt.value) {
                            baseItem.exclude_var = true;
                        }
                        if (opt.name === "REPLAY" && !opt.value) {
                            baseItem.exclude_replay = true;
                        }
                    });
                }
                else if (km.options.schema === HIGHLIGHT_OPTION_SHEMA.RADIO_BUTTON) {
                    const selectedOption = km.options.values.find(opt => opt.value);

                    if (selectedOption && selectedOption.name === "RED_ONLY") {
                        isRedOnly = true;
                    }
                }
            }

            if (isRedOnly) {
                return [
                    { ...baseItem, attributes: ["RED_CARD"] },
                    { ...baseItem, attributes: ["SECOND_YELLOW"] }
                ];
            }

            return [baseItem];
        });

    return { items };
};